/**
 * Klarna On-Site Messaging: keep the badge's purchase amount in step with the
 * selected variant.
 *
 * This replaces the Klarna app block, which shipped the entire variant array
 * into the page twice so its own script could do the same job client side.
 * See snippets/klarna-osm.liquid for the full story.
 *
 * How the update is triggered
 *   theme.js dispatches two events from one call site (the _onSelectChange
 *   handler, around line 983):
 *     container.dispatchEvent(new CustomEvent('variantChange', {detail}))
 *     document.dispatchEvent(new CustomEvent('variant:change', {detail}))
 *   'variantChange' does not bubble, so a listener on document only sees it in
 *   the CAPTURE phase. Capture runs from window down to the target regardless
 *   of the bubbles flag, so listening with capture:true catches every dispatch
 *   AND gives us evt.target, the product container that actually changed. That
 *   is what lets a page with more than one product container (quick shop
 *   modal, for instance) update only its own placement.
 *
 * How the refresh is triggered
 *   Read out of https://js.klarna.com/web-sdk/v1/klarna.js itself, not guessed:
 *   the loader installs
 *     window.OnsiteMessaging   = { refresh: N, eventListeners: {}, on: ... }
 *     window.KlarnaOnsiteService = new A   // A has an own 'refresh' = N
 *   where N() is `document.dispatchEvent(new CustomEvent('OSM:refresh'))`, and
 *   window.Klarna.OnsiteMessaging appears once the full SDK has initialised
 *   (the SDK's own isWebSDKAvailable probe checks for it). We try the public
 *   documented entry point first and fall back down that chain, ending at the
 *   raw OSM:refresh event. Every path is guarded: the SDK script is async, so
 *   window.Klarna is routinely undefined while a shopper is already clicking
 *   swatches, and a missing badge must never break the variant picker.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-klarna-osm]';

  function refresh() {
    try {
      var k = window.Klarna;
      if (k && k.OnsiteMessaging && typeof k.OnsiteMessaging.refresh === 'function') {
        k.OnsiteMessaging.refresh();
        return;
      }
      if (window.OnsiteMessaging && typeof window.OnsiteMessaging.refresh === 'function') {
        window.OnsiteMessaging.refresh();
        return;
      }
      if (window.KlarnaOnsiteService && typeof window.KlarnaOnsiteService.refresh === 'function') {
        window.KlarnaOnsiteService.refresh();
        return;
      }
      document.dispatchEvent(new CustomEvent('OSM:refresh'));
    } catch (e) {
      /* A Klarna badge is never worth throwing on a product page. */
    }
  }

  /**
   * Write the amount onto every placement in scope.
   * Returns true only if something actually changed, so the two listeners
   * below can both be registered without ever double-refreshing.
   */
  function apply(scope, price) {
    var amount = parseInt(price, 10);
    if (isNaN(amount)) return false;
    var value = String(amount);

    var nodes = scope.querySelectorAll(SELECTOR);
    if (!nodes.length && scope !== document) {
      nodes = document.querySelectorAll(SELECTOR);
    }

    var changed = false;
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-purchase-amount') !== value) {
        nodes[i].setAttribute('data-purchase-amount', value);
        changed = true;
      }
    }
    return changed;
  }

  function onVariant(evt) {
    var variant = evt && evt.detail && evt.detail.variant;
    if (!variant) return;
    var scope = evt.target && typeof evt.target.querySelectorAll === 'function'
      ? evt.target
      : document;
    if (apply(scope, variant.price)) refresh();
  }

  document.addEventListener('variantChange', onVariant, true);
  document.addEventListener('variant:change', onVariant);
})();
